import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Send } from 'lucide-react';
import { reportsApi, repairRequestsApi } from '@/api';
import type { Report, ReportItem } from '@/types';
import { sortByOrder } from '@/utils/helpers';
import { Button, useToast } from '@/ui';
import { hexToRgbTriplet } from '../_shared';
import { PortalNotFound, PortalSkeleton } from '../states';
import { FixListBar } from './FixListBar';
import { StepSelectActions } from './StepSelectActions';
import { StepReview } from './StepReview';
import { StepConfirm } from './StepConfirm';
import { FIX_ACTION_BY_KEY, type FixActionKey, type FixFinding, type RecipientKind } from './types';
import { midRangeFromEstimate } from './_format';

type Step = 1 | 2 | 3 | 4;

const FixListPage: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>(1);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [actions, setActions] = useState<Map<string, FixActionKey>>(() => new Map());
  const [credits, setCredits] = useState<Map<string, number | undefined>>(() => new Map());
  const [recipientKind, setRecipientKind] = useState<RecipientKind>('inspector');
  const [listingAgentName, setListingAgentName] = useState('');
  const [listingAgentEmail, setListingAgentEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [notificationSent, setNotificationSent] = useState(true);

  useEffect(() => {
    if (!publicId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await reportsApi.getPublicReport(publicId);
        if (cancelled) return;
        if (res.success && res.data) {
          setReport(res.data);
        } else {
          setError('Report not found or is no longer available.');
        }
      } catch {
        if (!cancelled) setError('Failed to load report. Please check the link and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [publicId]);

  // Flatten the section → items tree into the FixList finding shape, mirroring
  // the report's own "Findings at a glance" indexing so IDs match what the
  // buyer already saw on the report page.
  const findings: FixFinding[] = useMemo(() => {
    if (!report?.sections) return [];
    const out: FixFinding[] = [];
    const sortedSections = sortByOrder(report.sections).filter(s => (s.items?.length ?? 0) > 0);
    sortedSections.forEach((section, sectionIdx) => {
      const items = sortByOrder(section.items || []) as ReportItem[];
      items.forEach((item, itemIdx) => {
        out.push({
          id: item.id,
          sectionId: section.id,
          sectionTitle: section.title,
          indexLabel: `${sectionIdx + 1}.${itemIdx + 1}`,
          title: item.title,
          description: item.description ?? '',
          severity: item.severity,
          estimatedCost: item.estimatedCost,
          estimatedCostLow: item.estimatedCostLow,
          estimatedCostHigh: item.estimatedCostHigh,
          creditMid: midRangeFromEstimate(item.estimatedCostLow, item.estimatedCostHigh, item.estimatedCost),
        });
      });
    });
    return out;
  }, [report]);

  // Pre-select Safety Concerns + default action 'repair'. Runs once when
  // findings first arrive so re-renders from selection edits don't reset.
  const [primed, setPrimed] = useState(false);
  useEffect(() => {
    if (primed || findings.length === 0) return;
    const sel = new Set<string>();
    const acts = new Map<string, FixActionKey>();
    findings.forEach(f => {
      if (f.severity === 'safety_concern') sel.add(f.id);
      acts.set(f.id, 'repair');
    });
    setSelected(sel);
    setActions(acts);
    setPrimed(true);
  }, [findings, primed]);

  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const setAction = (id: string, action: FixActionKey) => setActions(prev => {
    const next = new Map(prev); next.set(id, action); return next;
  });
  const setCredit = (id: string, value: number | undefined) => setCredits(prev => {
    const next = new Map(prev);
    if (value === undefined) next.delete(id); else next.set(id, value);
    return next;
  });

  const goBack = () => {
    if (step === 1) navigate(`/r/${publicId}`);
    else if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
    else navigate(`/r/${publicId}`);
  };

  const hasFurtherInspect = useMemo(
    () => Array.from(selected).some(id => (actions.get(id) ?? 'repair') === 'further_inspection'),
    [selected, actions],
  );
  const ccInspector = recipientKind === 'listing_agent' && hasFurtherInspect;

  const recipientFormValid = recipientKind === 'inspector'
    || (listingAgentName.trim().length > 0 && /.+@.+\..+/.test(listingAgentEmail));

  const handleSubmit = async () => {
    if (!report) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const items = findings
        .filter(f => selected.has(f.id))
        .map(f => {
          const action = actions.get(f.id) ?? 'repair';
          const def = FIX_ACTION_BY_KEY[action];
          let cost: number | null = null;
          if (def.showCredit) {
            const explicit = credits.get(f.id);
            cost = explicit != null ? explicit : (midRangeFromEstimate(f.estimatedCostLow, f.estimatedCostHigh, f.estimatedCost) ?? null);
          }
          return {
            reportItemId: f.id,
            itemTitle: f.title,
            severity: f.severity === 'safety_concern' || f.severity === 'recommendation' ? f.severity : null,
            requestedAction: action,
            justification: '',
            estimatedCost: cost,
          };
        });

      const payload = {
        reportId: report.id,
        recipientKind,
        ...(recipientKind === 'listing_agent' ? {
          recipientName: listingAgentName.trim(),
          recipientEmail: listingAgentEmail.trim(),
        } : {}),
        requestedItems: items,
      };

      const res = await repairRequestsApi.createRepairRequest(payload as any);
      if (res.success && res.data) {
        const notif = (res.data as any).notification;
        setNotificationSent(notif ? Boolean(notif.sent) : true);
        setStep(4);
        window.scrollTo({ top: 0, behavior: 'auto' });
      } else {
        const msg = res.error || 'Failed to send your fix list. Please try again.';
        setSubmitError(msg);
        toast.show({ tone: 'critical', title: "Couldn't send your fix list", description: msg });
      }
    } catch {
      const msg = 'Failed to send your fix list. Please try again.';
      setSubmitError(msg);
      toast.show({ tone: 'critical', title: "Couldn't send your fix list", description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <PortalSkeleton />;
  if (error || !report) return <PortalNotFound message={error} />;

  const brand = report.inspector ?? null;
  const brandRgb = hexToRgbTriplet(brand?.brandColor);
  const inspectorName = [brand?.firstName, brand?.lastName].filter(Boolean).join(' ') || 'Your Inspector';
  const inspectorFirst = inspectorName.split(/\s+/)[0] || 'inspector';

  // Sender = buyer's agent if present, else the buyer themselves. Mirrors the
  // backend's preference order so the "Prepared by" line on Step 3 matches
  // the realtor_* row stored at submit time.
  const preparedByName = report.agentName?.trim() || report.clientName?.trim() || 'the buyer';
  const propertyAddress = report.propertyAddress || 'Your inspection';
  const preparedDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const sendToFirst = recipientKind === 'listing_agent'
    ? (listingAgentName.trim() ? listingAgentName.trim().split(/\s+/)[0] : 'agent')
    : inspectorFirst;
  const sendToFull = recipientKind === 'listing_agent'
    ? (listingAgentName.trim() || 'the listing agent')
    : inspectorName;

  const stepLabel: Record<1 | 2 | 3, string> = {
    1: 'Pick items',
    2: 'Choose actions',
    3: 'Review',
  };

  const primary =
    step === 1 ? (
      <Button variant="primary" onClick={() => setStep(2)} disabled={selected.size === 0} trailingIcon={<ArrowRight />}>
        <span className="hidden sm:inline">Continue · {selected.size} {selected.size === 1 ? 'item' : 'items'}</span>
        <span className="sm:hidden">Continue</span>
      </Button>
    ) : step === 2 ? (
      <Button variant="primary" onClick={() => setStep(3)} disabled={selected.size === 0} trailingIcon={<ArrowRight />}>
        Review
      </Button>
    ) : step === 3 ? (
      <Button
        variant="primary"
        onClick={handleSubmit}
        loading={submitting}
        disabled={selected.size === 0 || !recipientFormValid}
        leadingIcon={<Send />}
      >
        <span className="hidden sm:inline">Send to {sendToFirst}</span>
        <span className="sm:hidden">Send</span>
      </Button>
    ) : null;

  const safetyCount = findings.filter(f => selected.has(f.id) && f.severity === 'safety_concern').length;
  const recommendCount = findings.filter(f => selected.has(f.id) && f.severity === 'recommendation').length;
  const totalCredits = findings
    .filter(f => selected.has(f.id))
    .reduce((s, f) => {
      const a = actions.get(f.id) ?? 'repair';
      if (!FIX_ACTION_BY_KEY[a].showCredit) return s;
      const explicit = credits.get(f.id);
      const v = explicit != null ? explicit : (midRangeFromEstimate(f.estimatedCostLow, f.estimatedCostHigh, f.estimatedCost) ?? 0);
      return s + v;
    }, 0);

  return (
    <div
      className="min-h-screen bg-surface-0 brand-root"
      style={{ '--brand': brandRgb } as React.CSSProperties}
    >
      <FixListBar
        propertyAddress={propertyAddress}
        step={step === 4 ? undefined : (step as 1 | 2 | 3)}
        stepLabel={step === 4 ? undefined : stepLabel[step as 1 | 2 | 3]}
        onBack={step === 4 ? () => navigate(`/r/${publicId}`) : goBack}
        backLabel={step === 1 || step === 4 ? 'Back to report' : 'Back'}
        primary={step === 4 ? null : primary}
      />

      {step === 1 || step === 2 ? (
        <StepSelectActions
          step={step}
          findings={findings}
          selected={selected}
          actions={actions}
          credits={credits}
          onToggle={toggle}
          onAction={setAction}
          onCredit={setCredit}
          onContinue={() => setStep(step === 1 ? 2 : 3)}
        />
      ) : step === 3 ? (
        <>
          <StepReview
            findings={findings}
            selected={selected}
            actions={actions}
            credits={credits}
            preparedByName={preparedByName}
            preparedDate={preparedDate}
            propertyAddress={propertyAddress}
            inspectorName={inspectorName}
            inspectorCompany={brand?.companyName ?? null}
            inspectorLicense={brand?.licenseNumber ?? null}
            recipientKind={recipientKind}
            onRecipientKindChange={setRecipientKind}
            listingAgentName={listingAgentName}
            listingAgentEmail={listingAgentEmail}
            onListingAgentNameChange={setListingAgentName}
            onListingAgentEmailChange={setListingAgentEmail}
            submitting={submitting}
            submitError={submitError}
            onSubmit={handleSubmit}
          />
          {/* Mobile sticky bottom bar — Send sits in the persistent footer
              since the desktop action row is hidden on small screens. */}
          <div
            className="fixed bottom-0 inset-x-0 z-30 sm:hidden border-t border-border-subtle bg-surface-0/95 backdrop-blur"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="max-w-[860px] mx-auto px-4 py-3">
              <Button
                variant="primary"
                block
                onClick={handleSubmit}
                loading={submitting}
                disabled={selected.size === 0 || !recipientFormValid}
                leadingIcon={<Send />}
              >
                Send to {sendToFirst}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <StepConfirm
          recipientName={sendToFull}
          ccInspectorName={ccInspector ? inspectorName : null}
          itemCount={selected.size}
          safetyCount={safetyCount}
          recommendCount={recommendCount}
          totalCredits={totalCredits}
          notificationSent={notificationSent}
          onBack={() => navigate(`/r/${publicId}`)}
        />
      )}
    </div>
  );
};

export default FixListPage;
